import { useCurrentUserContext } from '../../../../context/currentUser/CurrentUserContext';
import { useCreateSmallDocumentWithMetadata } from '../../../../context/documents/useCreateSmallDocumentWithMetadata';
import { useUpdateSmallDocumentWithMetadata } from '../../../../context/documents/useUpdateSmallDocumentWithMetadata';
import { GearDocumentMetadata } from '../../../../Model/GearDocumentMetadata';
import { CreateSmallDocumentWithMetadataRequest } from '../../../../Model/requests/CreateSmallDocumentWithMetadataRequest';
import { UpdateSmallDocumentWithMetadataRequest } from '../../../../Model/requests/UpdateSmallDocumentWithMetadataRequest';

interface UseSmallFileUploadParams {
  isNewDocument: boolean;
}

interface SmallFileCreateParams {
  file: File;
  ctName: string;
  metadata: GearDocumentMetadata;
  retry?: boolean;
}

interface SmallFileUpdateParams {
  file: File;
  ctName: string;
  documentId: string;
  metadata: GearDocumentMetadata;
  etag?: string;
  retry?: boolean;
}

interface SmallFileUploadOptions {
  onSuccess?: () => void;
}

export const useSmallFileUpload = ({ isNewDocument }: UseSmallFileUploadParams) => {
  const { selectedIndex } = useCurrentUserContext();
  const { mutate: mutateCreateSmallDocumentWithMetadata, isPending: isCreating } = useCreateSmallDocumentWithMetadata();
  const { mutate: mutateUpdateSmallDocumentWithMetadata, isPending: isUpdating } = useUpdateSmallDocumentWithMetadata();

  const createSmallFile = (params: SmallFileCreateParams, options?: SmallFileUploadOptions) => {
    const dto: CreateSmallDocumentWithMetadataRequest = {
      ctName: params.ctName,
      indexId: selectedIndex?.id ?? -1,
      file: params.file,
      metadata: params.metadata
    };

    mutateCreateSmallDocumentWithMetadata({ dto, retry: params.retry }, { onSuccess: options?.onSuccess });
  };

  const updateSmallFile = (params: SmallFileUpdateParams, options?: SmallFileUploadOptions) => {
    const dto: UpdateSmallDocumentWithMetadataRequest = {
      ctName: params.ctName,
      documentId: params.documentId,
      indexId: selectedIndex?.id ?? -1,
      file: params.file,
      metadata: params.metadata
    };

    mutateUpdateSmallDocumentWithMetadata(
      { dto, etag: params.etag, retry: params.retry },
      { onSuccess: options?.onSuccess }
    );
  };

  const uploadSmallFile = (params: SmallFileCreateParams | SmallFileUpdateParams, options?: SmallFileUploadOptions) => {
    if (isNewDocument) {
      createSmallFile(params as SmallFileCreateParams, options);
    } else {
      updateSmallFile(params as SmallFileUpdateParams, options);
    }
  };

  return {
    uploadSmallFile,
    createSmallFile,
    updateSmallFile,
    isLoading: isCreating || isUpdating
  };
};
